import { useCallback, useRef } from "react";
import { Button, Form, List, Modal, Tabs } from "../../components";
import {
  BankOutlined,
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { Form as AntForm } from "antd";
import { useApp, useAuth, useForm } from "../../hooks";
import { ModalRef } from "../../types/refs";
import { Address, AddressType } from "../../types/item";
import APIs from "../../apis";

interface Props {
  bordered?: boolean;
  onSelect?: (data: Address) => void;
}

const addressTypes: AddressType[] = ["home", "office", "other"];

const kinds: {
  value: AddressType;
  label: string;
  icon: typeof HomeOutlined;
}[] = [
  { value: "home", label: "Home", icon: HomeOutlined },
  { value: "office", label: "Office", icon: BankOutlined },
  { value: "other", label: "Other", icon: EnvironmentOutlined },
];

const isAddressType = (value: unknown): value is AddressType =>
  addressTypes.includes(value as AddressType);

const addressTypeTabs = kinds.map((kind) => {
  const Icon = kind.icon;
  return {
    value: kind.value,
    label: kind.label,
    icon: <Icon />,
  };
});

const AddressIcon = ({ kind }: { kind: AddressType }) => {
  const match = kinds.find((item) => item.value === kind) ?? kinds[2];
  const Icon = match.icon;
  return (
    <Icon className="flex h-[4rem] w-[4rem] items-center justify-center rounded-full bg-primary-soft text-[1.8rem] text-primary" />
  );
};

const AddAddress = ({ onSelect }: Props) => {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const { modal } = useApp();
  const addressForm = useForm({ initialValues: { type: "other" } });
  const editingRef = useRef<Address | undefined>(undefined);
  const addAddressModalRef = useRef<ModalRef>({
    open: () => {},
    close: () => {},
  });

  const addressPayload = ({ type, nearest, ...data }: any, editing = false) => {
    const landmark = typeof nearest === "string" ? nearest.trim() : "";
    return {
      ...data,
      ...(landmark ? { nearest: landmark } : editing ? { nearest: null } : {}),
      ...(isAddressType(type) ? { type } : {}),
    };
  };

  const saveAddress = async (values: any) => {
    const editing = editingRef.current;
    try {
      if (editing)
        await APIs.auth.updateAddress(
          editing.uuid,
          addressPayload(values, true),
        );
      else await APIs.auth.createAddress(addressPayload(values));
      editingRef.current = undefined;
      await refreshUser();
      addressForm.resetFields();
      addAddressModalRef.current.close();
    } catch (error) {}
  };

  const openAddress = (address?: Address) => {
    editingRef.current = address;
    addAddressModalRef.current.open(address);
    if (!address) {
      addressForm.resetFields();
      return;
    }
    addressForm.setFieldsValue({
      type: isAddressType(address.type) ? address.type : "other",
      street: address.street,
      nearest: address.nearest ?? "",
    });
  };

  const renderAddAddressInfo = useCallback(
    ({ onSave = (data: any) => {} } = {}) => {
      return (
        <>
          <Form
            form={addressForm}
            renderFormItem={(field) =>
              field.name === "type" ? (
                <AntForm.Item name="type" className="m-0 w-100">
                  <Tabs items={addressTypeTabs} />
                </AntForm.Item>
              ) : null
            }
            fields={[
              { name: "type" },
              {
                name: "street",
                label: "Address",
                placeholder: "Enter your street address",
                rules: [{ required: true }],
              },
              {
                name: "nearest",
                label: "Nearest landmark (Optional)",
                placeholder: "Enter your nearest landmark",
              },
            ]}
          />
          <Button
            title="Save"
            className="w-100"
            minWidth="100%"
            style={{ marginTop: 45 }}
            onClick={async () => {
              await addressForm.validateFields();
              onSave(addressForm.getFieldsValue());
            }}
          />
        </>
      );
    },
    [addressForm],
  );

  if (!isAuthenticated)
    return renderAddAddressInfo({
      onSave: ({ type, nearest, ...address }) => {
        const landmark = typeof nearest === "string" ? nearest.trim() : "";
        onSelect!({
          ...address,
          ...(landmark ? { nearest: landmark } : {}),
          ...(isAddressType(type) ? { type } : {}),
          address: [address.street, landmark].filter(Boolean).join(" "),
        });
      },
    });
  return (
    <>
      <List
        bordered
        className="list m-30-b"
        hoverable={!!onSelect}
        dataSource={user?.addresses ?? []}
        empty={{ description: "No saved addresses." }}
        renderItem={(address) => (
          <List.Item
            onClick={!onSelect ? () => {} : () => onSelect(address)}
            extra={
              <div className="flex items-center gap-[1.2rem]">
                <Button
                  type="text"
                  color="secondary"
                  icon={<EditOutlined />}
                  onClick={() => openAddress(address)}
                />
                <Button
                  type="text"
                  color="base"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    modal.confirm({
                      title: "Delete address?",
                      description:
                        "This address will be removed from your saved addresses.",
                      confirmText: "Delete",
                      onConfirm: async () => {
                        await APIs.auth.deleteAddress(address.uuid);
                        await refreshUser();
                      },
                    });
                  }}
                />
              </div>
            }
          >
            <List.Item.Meta
              avatar={
                <AddressIcon
                  kind={isAddressType(address.type) ? address.type : "other"}
                />
              }
              title={address.street || address.address}
              description={address.nearest || undefined}
            />
          </List.Item>
        )}
      />
      <Button title="Create New Address" onClick={() => openAddress()} />
      <Modal
        title={(address?: Address) =>
          address ? "Edit address" : "Add new Address"
        }
        ref={addAddressModalRef}
        children={renderAddAddressInfo({ onSave: saveAddress })}
      />
    </>
  );
};

export default AddAddress;
